import { useCallback, useState } from 'react';

import type { IPostApplication } from 'services/applications/applications.api.type';
import type { IApplicationImageResource } from 'interfaces/ApplicationImage.interface';
import type { IApplicationDocument } from 'interfaces/ApplicationDocument.interface';
import type { IApplicationDocumentFileEntry } from 'services/applicationDocumentsFiles/applicationDocumentFiles.api.types';
import type { IApplicationDocumentFilesData } from 'pages/public/JobAdApplyForm/JobAdApplyForm.interface';
import {
  usePostApplicationImagePublicEntityItemMutation,
  usePostApplicationImagePublicItemMutation,
  usePostPublicApplicationItemMutation,
  usePostPublicApplicationDocumentItemMutation,
  usePostPublicApplicationDocumentFileItemMutation,
  usePostPublicApplicationDocumentFileEntityItemMutation,
} from 'services/publicTeamHeroApi.service';
import { useErrorHandler } from 'hooks/useDisplayApiError.hook';

interface IApplicationData extends IPostApplication {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [property: string]: any;
}

interface IUseCreateApplicationReturn {
  createApplicationFlow: (
    applicationData: IApplicationData,
    applicationDocumentFilesData: IApplicationDocumentFilesData[]
  ) => Promise<void>;
  isLoading: boolean;
}

export const useCreateApplication = (
  onSuccess: () => void
): IUseCreateApplicationReturn => {
  const { handleError } = useErrorHandler();
  const [createApplication, { isLoading: isPostingApplication }] =
    usePostPublicApplicationItemMutation();

  const [isLoading, setIsLoading] = useState(false);

  const [createApplicationImageEntity] =
    usePostApplicationImagePublicEntityItemMutation();

  const [createApplicationDocumentEntity] =
    usePostPublicApplicationDocumentItemMutation();

  const [addApplicationImageFile] = usePostApplicationImagePublicItemMutation();

  const [createDocumentFileEntity] =
    usePostPublicApplicationDocumentFileEntityItemMutation();
  const [addDocumentFileBinary] =
    usePostPublicApplicationDocumentFileItemMutation();

  const createApplicationImages = useCallback(
    (files: File[], applicationIri: string) =>
      Promise.all(
        files.map((file, index) => {
          return createApplicationImageEntity({
            application: applicationIri,
            type: index === 0 ? 'profile' : 'body',
          })
            .unwrap()
            .then((result: IApplicationImageResource) => {
              const formData = new FormData();
              formData.append('file', file);

              return addApplicationImageFile({
                id: result.id.toString(),
                body: formData,
              }).unwrap();
            });
        })
      ),
    [addApplicationImageFile, createApplicationImageEntity]
  );

  const createApplicationDocuments = useCallback(
    (
      applicationDocumentFilesData: IApplicationDocumentFilesData[],
      applicationIri: string
    ) =>
      Promise.all(
        applicationDocumentFilesData.map((filesData) => {
          return createApplicationDocumentEntity({
            body: {
              application: applicationIri,
              template: filesData.template,
            },
          })
            .unwrap()
            .then((result: IApplicationDocument) => {
              const formData = new FormData();
              formData.append('file', filesData.file);

              return createDocumentFileEntity({
                fileFor: result['@id'],
              })
                .unwrap()
                .then((fileEntry: IApplicationDocumentFileEntry) => {
                  return addDocumentFileBinary({
                    id: fileEntry.id,
                    body: formData,
                  }).unwrap();
                });
            });
        })
      ),
    [
      addDocumentFileBinary,
      createApplicationDocumentEntity,
      createDocumentFileEntity,
    ]
  );

  const createApplicationFlow = useCallback(
    async (
      applicationData: IApplicationData,
      applicationDocumentFilesData: IApplicationDocumentFilesData[]
    ): Promise<void> => {
      setIsLoading(true);
      const images = (applicationData.avatar as File[]) || [];

      try {
        // application creation
        const applicationResponse = await createApplication({
          body: applicationData,
        }).unwrap();

        // create associated images and documents
        if (images.length) {
          await createApplicationImages(images, applicationResponse['@id']);
        }

        if (applicationDocumentFilesData.length) {
          await createApplicationDocuments(
            applicationDocumentFilesData,
            applicationResponse['@id']
          );
        }
        onSuccess();
        setIsLoading(false);
      } catch (error) {
        handleError(error);
        setIsLoading(false);
      }
    },
    [
      createApplication,
      createApplicationDocuments,
      createApplicationImages,
      handleError,
      onSuccess,
    ]
  );

  return {
    createApplicationFlow,
    isLoading: isLoading || isPostingApplication,
  };
};
